import React, { useState, useMemo, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Menu, X, Home, BookOpen, Award, Phone, User, LogOut,
  BarChart, Users, Package, Calendar, ChevronDown, Search
} from 'lucide-react';

interface LayoutProps {
  isAdmin?: boolean;
  isFormateur?: boolean;
}

const Layout: React.FC<LayoutProps> = ({ isAdmin = false, isFormateur = false }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { user, logout } = useAuth();
  const location = useLocation();

  // Gestion de la fermeture du panneau utilisateur avec la touche Escape
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && userMenuOpen) {
        setUserMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [userMenuOpen]);

  const navigation = isAdmin ? [
    { name: 'Tableau de bord', href: '/admin/dashboard', icon: Home },
    { name: 'Produits', href: '/admin/products', icon: Package },
    { name: 'Promotions', href: '/admin/promotions', icon: Calendar },
    { name: 'Utilisateurs', href: '/admin/users', icon: Users },
    { name: 'Statistiques', href: '/admin/stats', icon: BarChart },
    { name: 'Modules', href: '/admin/modules', icon: BookOpen },
    { name: 'Simulations', href: '/admin/simulations', icon: Phone },
  ] : isFormateur ? [
    { name: 'Tableau de bord', href: '/formateur/dashboard', icon: Home },
    { name: 'Produits', href: '/formateur/products', icon: Package },
    { name: 'Promotions', href: '/formateur/promotions', icon: Calendar },
    { name: 'Utilisateurs', href: '/formateur/users', icon: Users },
    { name: 'Simulation', href: '/formateur/simulation', icon: Phone },
    { name: 'Profil', href: '/formateur/profile', icon: User },
  ] : [
    { name: 'Tableau de bord', href: '/dashboard', icon: Home },
    { name: 'Promotions', href: '/promotions', icon: BookOpen },
    { name: 'Simulations', href: '/simulations', icon: Phone },
    { name: 'Mon Profil', href: '/profile', icon: User },
  ];

  // Filtrer les éléments de navigation basés sur la recherche
  const filteredNavigation = useMemo(() => {
    if (!searchQuery.trim()) return navigation;
    return navigation.filter(item => 
      item.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [navigation, searchQuery]);

  const renderSidebarNavigation = (items = filteredNavigation) =>
    items.map((item) => {
      const Icon = item.icon;
      const isActive = location.pathname === item.href;
      const isHighlighted = searchQuery.trim() && item.name.toLowerCase().includes(searchQuery.toLowerCase());
      return (
        <Link
          key={item.name}
          to={item.href}
          aria-current={isActive ? 'page' : undefined}
          className={`group relative flex items-center gap-3 px-3.5 py-2.5 rounded-lg min-h-[44px] select-none transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/60 active:scale-[0.98] ${
            isActive
              ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 text-white shadow-md ring-1 ring-indigo-500/40 hover:shadow-lg hover:-translate-y-0.5'
              : isHighlighted
              ? 'bg-amber-50 text-amber-800 ring-1 ring-amber-200 hover:ring-amber-300 hover:bg-amber-100'
              : 'text-gray-700/90 ring-1 ring-transparent hover:ring-indigo-200 hover:bg-indigo-50 hover:text-indigo-700 hover:shadow-sm'
          }`}
          onClick={() => setSidebarOpen(false)}
        >
          <Icon className={`w-5 h-5 shrink-0 transition-transform duration-200 ${
            isActive ? 'text-white' : 'text-gray-400 group-hover:text-indigo-600 group-hover:scale-110'
          }`} />
          <span className="font-medium tracking-wide truncate">{item.name}</span>
        </Link>
      );
    });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Overlay pour fermer le sidebar */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-25 z-40 transition-opacity duration-300 ease-in-out"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed top-0 left-0 h-full w-80 md:w-80 sm:w-72 bg-white shadow-2xl z-50 transform transition-all duration-300 ease-in-out ${
        sidebarOpen ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-95'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo dans le sidebar et bouton de fermeture */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
            {/* Logo TELEXCEL dans le sidebar */}
            <div className={`flex-1 flex justify-center transition-all duration-300 ease-out ${
              sidebarOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
            }`}>
              <img
                src="/logo/TELEXCEL_Logo_sidebar.png"
                alt="TELEXCEL"
                className="h-8 w-auto object-contain filter drop-shadow-sm"
                style={{ 
                  imageRendering: 'crisp-edges',
                  maxWidth: '80px',
                  height: '48px'
                }}
              />
            </div>
            
            {/* Bouton de fermeture */}
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-all duration-200 hover:scale-105 ml-2"
            >
              <X className="w-5 h-5" />
            </button>
          </div>


          {/* Navigation dans le sidebar */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <nav className="space-y-3">
              {filteredNavigation.length > 0 ? (
                renderSidebarNavigation()
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500 text-sm">Aucun résultat trouvé pour "{searchQuery}"</p>
                </div>
              )}
            </nav>
          </div>

        </div>
      </div>

      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-30">
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-16 2xl:px-24">
          <div className="flex justify-between items-center h-16">
            {/* Bouton hamburger + Logo */}
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 rounded-lg text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 hover:scale-105"
              >
                <Menu className="w-6 h-6" />
              </button>

              {/* Logo TELEXCEL avec texte dans le header - affiché seulement quand sidebar fermé */}
              <div className={`flex items-center space-x-3 transition-all duration-300 ease-out ${
                sidebarOpen ? 'opacity-0 scale-95 -translate-x-4' : 'opacity-100 scale-100 translate-x-0'
              }`}>
                <img
                  src="/logo/TELEXCEL_Logo_sidebar.png"
                  alt="TELEXCEL"
                  className="h-8 sm:h-10 w-auto object-contain filter drop-shadow-sm"
                  style={{ 
                    imageRendering: 'crisp-edges',
                    maxWidth: '80px',
                    height: '48px'
                  }}
                />
                <span className="text-lg sm:text-xl font-semibold text-gray-900 hidden sm:block">
                  TELEXCEL FORMATION
                </span>
              </div>
            </div>

            {/* Barre de recherche centrée */}
            <div className="hidden md:flex flex-1 max-w-2xl mx-6">
              <div className="relative w-full">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Rechercher dans le menu..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 pr-4 py-2.5 w-full text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-gray-50 focus:bg-white shadow-sm hover:shadow-md"
                />
              </div>
            </div>

            {/* Menu utilisateur */}
            <div className="flex items-center">
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-3 px-3 py-2 rounded-xl text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200 hover:scale-105"
                  aria-expanded={userMenuOpen}
                  aria-haspopup="true"
                >
                  <div className="w-8 h-8 bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-full flex items-center justify-center shadow-md">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm font-medium hidden lg:block truncate max-w-32">{user?.prenom}</span>
                  <ChevronDown className={`w-4 h-4 hidden lg:block transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Panneau utilisateur modernisé */}
                {userMenuOpen && (
                  <div className="absolute right-0 mt-3 w-80 sm:w-96 max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-gray-100 z-[9999] transform transition-all duration-200 ease-out scale-100 opacity-100 animate-in slide-in-from-top-2">
                    {/* En-tête de la carte avec informations utilisateur */}
                    <div className="p-4 sm:p-5 pb-4">
                      <div className="flex items-start space-x-3 sm:space-x-4">
                        {/* Avatar utilisateur */}
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-700 rounded-full flex items-center justify-center shadow-lg ring-2 ring-white">
                            <User className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                          </div>
                        </div>
                        
                        {/* Informations utilisateur */}
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col space-y-1">
                            <h3 className="text-sm sm:text-base font-semibold text-gray-900 truncate leading-tight">
                              {user?.prenom} {user?.nom}
                            </h3>
                            <p className="text-xs sm:text-sm text-gray-500 capitalize truncate">
                              {user?.role === 'agent' ? 'Agent' : 
                               user?.role === 'admin' ? 'Administrateur' :
                               user?.role === 'formateur' ? 'Formateur' : user?.role}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Séparateur visuel */}
                    <div className="border-t border-gray-100"></div>

                    {/* Section des actions */}
                    <div className="p-3">
                      <button
                        onClick={() => {
                          logout();
                          setUserMenuOpen(false);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            logout();
                            setUserMenuOpen(false);
                          }
                          if (e.key === 'Escape') {
                            setUserMenuOpen(false);
                          }
                        }}
                        className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-red-50 hover:text-red-600 focus:bg-red-50 focus:text-red-600 active:bg-red-100 active:scale-[0.98] rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-white cursor-pointer group disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-gray-50 disabled:hover:text-gray-700"
                        aria-label="Se déconnecter de l'application"
                        tabIndex={0}
                      >
                        <LogOut className="w-4 h-4 mr-3 transition-transform duration-200 group-hover:scale-110 group-disabled:scale-100" />
                        <span>Déconnexion</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>


          </div>
        </div>
      </header>

      {/* Main content avec décalage dynamique */}
      <main className={`transition-all duration-300 ease-in-out ${
        sidebarOpen ? 'lg:ml-80 md:ml-80 sm:ml-0' : 'ml-0'
      }`}>
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-16 2xl:px-24 py-6 sm:py-8">
          <div className="w-full">
            <Outlet />
          </div>
        </div>
      </main>

      {/* Click outside handler pour le menu utilisateur */}
      {userMenuOpen && (
        <div
          className="fixed inset-0 z-20"
          onClick={() => setUserMenuOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout;
